import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Activity, ArrowLeft } from "lucide-react";
import { getStravaInviteState } from "../../lib/api";

/**
 * Public landing page the athlete lands on after clicking the email
 * invite. Looks up the one-time state and shows a friendly "Connect
 * Strava" CTA that bounces to the Strava OAuth URL. The athlete doesn't
 * need to be logged into the portal first — the state token IS the
 * authorization (single-use; expires after first use via the existing
 * callback flow).
 */
export default function StravaConnectLandingPage() {
  const [sp] = useSearchParams();
  const state = sp.get("state");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!state) {
      setError("This invite link is incomplete. Ask your coach to resend it.");
      return;
    }
    getStravaInviteState(state)
      .then(setInfo)
      .catch((e) =>
        setError(
          e?.response?.data?.detail ||
            "This invite is no longer valid. Ask your coach to send a fresh one.",
        ),
      );
  }, [state]);

  return (
    <div
      className="min-h-screen bg-[var(--tir-cream)] flex items-center justify-center px-6 py-12"
      data-testid="strava-landing-page"
    >
      <div className="max-w-lg w-full bg-white border border-[var(--tir-border)] p-8 sm:p-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-4">
          The Intuitive Runner
        </div>
        <div className="flex items-center gap-3 mb-4">
          <Activity size={28} className="text-[#fc4c02]" />
          <h1 className="font-serif-display text-3xl leading-tight">Connect Strava</h1>
        </div>

        {error ? (
          <div className="text-sm text-[var(--tir-burgundy)] mt-2 leading-relaxed">
            {error}
          </div>
        ) : !info ? (
          <div className="text-sm text-[var(--tir-muted)]">Loading…</div>
        ) : (
          <>
            <p className="text-[15px] leading-relaxed text-[var(--tir-ink)] mb-2">
              Hi{info.athlete_name ? ` ${info.athlete_name.split(" ")[0]}` : ""},
              your coach has invited you to connect your Strava account.
            </p>
            <p className="text-sm leading-relaxed text-[var(--tir-muted)] mb-6">
              Once connected, your runs auto-import into your training portal
              and match to the prescribed workout on each day — no more
              manual logging. We only read your activity data (distance,
              time, pace). You can disconnect any time from your portal.
            </p>
            <a
              href={info.strava_auth_url}
              data-testid="strava-landing-connect-btn"
              className="inline-flex items-center justify-center gap-2 w-full bg-[#fc4c02] hover:bg-[#e24400] text-white px-6 py-3.5 text-xs tracking-[0.22em] uppercase font-semibold transition-colors"
            >
              <Activity size={14} /> Connect with Strava
            </a>
            <p className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mt-4 text-center">
              You'll be sent to Strava to authorize. We'll bring you back here when you're done.
            </p>
          </>
        )}

        <Link
          to="/athlete/home"
          className="mt-6 inline-flex items-center gap-1 text-xs tracking-[0.18em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] transition-colors"
        >
          <ArrowLeft size={12} /> Athlete portal
        </Link>
      </div>
    </div>
  );
}
